import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { searchApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { quoteAmount } from './quote/plain-price.tsx';
import { formatLocalTime, formatShortDate } from '../i18n/datetime.ts';
import { classes, Spinner } from './ui.tsx';

/**
 * The one search box, opened with ⌘K (Ctrl+K) or the button in the header.
 *
 * WHAT PROBLEM IT SOLVES. This app has more than twenty pages and every list
 * had its own filter, which meant "where is the Okonkwo job?" was a question
 * you answered by first deciding which page it was a question about. One box
 * that searches customers, properties, quotes and visits at once removes that
 * step, and the same box navigates to pages, so the keyboard route to any
 * screen is the same three keystrokes.
 *
 * THE SERVER DECIDES WHAT MAY BE SEARCHED. Sections are omitted from its
 * response — not returned empty — when a role or a plan does not cover them
 * (routes/search.ts), so this renders whatever came back and never reasons
 * about permissions itself. The page shortcuts below are the one exception:
 * they are client-side navigation, so they are filtered by the same
 * permission and plan checks the navigation bar uses, passed in by the layout
 * that already computed them.
 */

/** A row in the palette: something to go to. */
interface Item {
  key: string;
  label: string;
  hint?: string | null;
  to: string;
}

interface Group {
  key: string;
  heading: string;
  items: Item[];
}

/** How long after the last keystroke the request goes out. */
const DEBOUNCE_MS = 250;

/** Below this the server refuses anyway (schemas/search.ts), so nothing is sent. */
const MIN_TERM_LENGTH = 2;

export interface PaletteShortcut {
  to: string;
  label: string;
}

export default function CommandPalette({
  open,
  onClose,
  shortcuts,
}: {
  open: boolean;
  onClose: () => void;
  /** The pages this user can reach, already filtered by permission and plan. */
  shortcuts: readonly PaletteShortcut[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company } = useSession();
  const listId = useId();
  const titleId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);

  // Everything resets on open rather than on close: a palette that clears
  // while it is fading out shows the clearing.
  useEffect(() => {
    if (!open) return;
    setTerm('');
    setDebounced('');
    setActive(0);
    const opener = document.activeElement;
    input.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const enabled = open && debounced.length >= MIN_TERM_LENGTH;
  const query = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => searchApi.query({ q: debounced }),
    enabled,
    // The previous term's results stay up while the next request is in
    // flight, so typing does not empty the list on every keystroke.
    placeholderData: keepPreviousData,
    // A search is a keystroke, not a subscription: refetching it when the
    // window regains focus would re-run a query nobody is looking at.
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  const currency = company?.currencyCode ?? 'USD';
  // Only the results that answer what is in the box RIGHT NOW.
  //
  // `keepPreviousData` is what stops the list flickering empty on every
  // keystroke, but it also means the rows on screen belong to the previous
  // term for as long as the debounce and then the request take — and those
  // rows are selectable. Enter during that window navigated to a record that
  // no longer matched anything the person had typed, and deleting back to one
  // character left the same rows live underneath "keep typing". The response
  // echoes the term it answered, so the check is the honest one rather than a
  // guess from loading flags: a row is shown when it is an answer to this
  // question.
  const results = query.data?.query === term.trim() ? query.data : undefined;

  const groups = useMemo<Group[]>(() => {
    const built: Group[] = [];
    const typed = term.trim().toLowerCase();

    // Page shortcuts match on the label the person can see, so "sch" finds
    // Schedule. Shown before the records when the box is empty — that is the
    // palette being a navigator — and after them once there is a term,
    // because then it is being a search.
    const matchingShortcuts = shortcuts
      .filter((shortcut) => typed.length === 0 || shortcut.label.toLowerCase().includes(typed))
      .map((shortcut) => ({ key: `page:${shortcut.to}`, label: shortcut.label, to: shortcut.to }));

    const pages: Group[] =
      matchingShortcuts.length > 0
        ? [{ key: 'pages', heading: t('commandPalette.pages'), items: matchingShortcuts }]
        : [];

    if (typed.length === 0) return pages;

    if (results?.customers?.length) {
      built.push({
        key: 'customers',
        heading: t('commandPalette.customers'),
        items: results.customers.map((hit) => ({
          key: `customer:${hit.id}`,
          label: hit.name || hit.companyName || t('commandPalette.unnamedCustomer'),
          hint: hit.email || hit.phone,
          to: `/customers/${hit.id}`,
        })),
      });
    }

    if (results?.properties?.length) {
      built.push({
        key: 'properties',
        heading: t('commandPalette.properties'),
        items: results.properties.map((hit) => ({
          key: `property:${hit.id}`,
          label: hit.address || hit.name || t('commandPalette.unnamedProperty'),
          hint: hit.name && hit.address ? hit.name : hit.postalCode,
          // Properties have no page of their own — they are edited on the
          // customer they belong to, which is where this goes.
          to: `/customers/${hit.customerId}`,
        })),
      });
    }

    if (results?.quotes?.length) {
      built.push({
        key: 'quotes',
        heading: t('commandPalette.quotes'),
        items: results.quotes.map((hit) => ({
          key: `quote:${hit.id}`,
          label: `${hit.quoteNumber} · ${hit.customerName}`,
          // The quote's own currency, not the company's today. A company that
          // switched still has old quotes denominated in the old one, and
          // relabeling the same integer changes what it means. A price range
          // reads as its band, as in the quote list: its total is the middle.
          hint: [quoteAmount({ ...hit, currencyCode: hit.currencyCode || currency }, t), hit.propertyAddress]
            .filter(Boolean)
            .join(' · '),
          to: `/quotes/${hit.id}`,
        })),
      });
    }

    if (results?.visits?.length) {
      built.push({
        key: 'visits',
        heading: t('commandPalette.visits'),
        items: results.visits.map((hit) => ({
          key: `visit:${hit.id}`,
          label: `${formatShortDate(hit.scheduledDate)} ${formatLocalTime(hit.startTimeLocal)} · ${hit.customerName}`,
          hint: hit.propertyAddress,
          // A single visit has no URL of its own; its schedule does, and that
          // page is where the visit and its siblings can be managed.
          to: `/schedule/series/${hit.seriesId}`,
        })),
      });
    }

    return [...built, ...pages];
  }, [results, shortcuts, term, t, currency]);

  const flat = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  // The highlight goes back to the top whenever the list changes underneath
  // it, so Enter never fires the row that happens to have inherited the index.
  useEffect(() => {
    setActive(0);
  }, [debounced, groups.length]);

  // Follow the highlight down the list.
  //
  // The listbox has a fixed height and scrolls, but focus never leaves the
  // input — the highlight is `aria-activedescendant`, not focus, and the
  // browser only scrolls to what it is focusing. So ArrowDown walked the
  // selection past the bottom of the box and Enter opened a row that had never
  // been on screen. `nearest` because this must not move the page itself when
  // the highlighted row is already visible.
  useEffect(() => {
    const key = flat[active]?.key;
    if (!key) return;
    const option = document.getElementById(`${listId}-${key}`);
    // jsdom has no layout and therefore no `scrollIntoView`; a test must not
    // die on a nicety.
    option?.scrollIntoView?.({ block: 'nearest' });
  }, [active, flat, listId]);

  if (!open) return null;

  function go(item: Item | undefined) {
    if (!item) return;
    onClose();
    navigate(item.to);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key === 'Tab') {
      // The palette is `aria-modal`, and the only thing in it that can take
      // focus is the input — the rows are `option`s, reached with the arrow
      // keys, not the tab order. So Tab would walk focus into the page behind
      // a dialog that claims to have covered it. Held here instead, the same
      // way the shared confirmation dialog holds it.
      event.preventDefault();
      input.current?.focus();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (flat.length === 0 ? 0 : (current + 1) % flat.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (flat.length === 0 ? 0 : (current - 1 + flat.length) % flat.length));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      go(flat[active]);
    }
  }

  const typed = term.trim();
  const tooShort = typed.length > 0 && typed.length < MIN_TERM_LENGTH;
  // The debounce counts as searching. `debounced` lags `term` by 250ms, so
  // between the second keystroke and the request going out there is a window
  // where nothing is in flight and nothing has matched — and "Nothing matched
  // that." would flash there on every single search anyone ever runs, which
  // is the opposite of what this flag is for.
  const settling = typed.length >= MIN_TERM_LENGTH && typed !== debounced;
  const searching = settling || (enabled && query.isFetching);
  // Only once the answer is in, and only for a term somebody actually typed:
  // an empty palette with no page shortcuts to offer is not a search that
  // found nothing.
  const empty = flat.length === 0 && typed.length > 0 && !searching && !tooShort;
  // A failed search is reported whatever else is on screen. Folded into
  // `empty` it was invisible in exactly the case that misleads most: a term
  // that also matches a page shortcut, which leaves a list that looks like a
  // successful answer to the term now in the box.
  const failed = enabled && query.isError;

  let index = -1;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-[color:var(--color-ink)]/50 p-4 pt-[10vh] text-start"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[calc(90dvh-1rem)] min-h-0 w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <h2 id={titleId} className="sr-only">
          {t('commandPalette.title')}
        </h2>
        <div className="flex shrink-0 items-center gap-3 border-b border-[color:var(--color-line)] px-4 py-3">
          <input
            ref={input}
            type="search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={flat[active] ? `${listId}-${flat[active].key}` : undefined}
            aria-label={t('commandPalette.inputLabel')}
            autoComplete="off"
            className="w-full border-0 bg-transparent text-base outline-none placeholder:text-[color:var(--color-muted)]"
            placeholder={t('commandPalette.placeholder')}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
          {searching ? <Spinner /> : null}
        </div>

        <ul
          id={listId}
          role="listbox"
          aria-label={t('commandPalette.resultsLabel')}
          className="min-h-0 max-h-96 overflow-y-auto py-2"
        >
          {groups.map((group) => (
            <li key={group.key} role="presentation">
              <p className="px-4 pt-3 pb-1 text-xs font-semibold tracking-wide text-[color:var(--color-muted)] uppercase">
                {group.heading}
              </p>
              <ul role="presentation">
                {group.items.map((item) => {
                  index += 1;
                  const position = index;
                  return (
                    <li
                      key={item.key}
                      id={`${listId}-${item.key}`}
                      role="option"
                      aria-selected={position === active}
                      className={classes(
                        'cursor-pointer px-4 py-2',
                        position === active
                          ? 'bg-[color:var(--color-brand-soft)]'
                          : 'hover:bg-[color:var(--color-surface)]',
                      )}
                      // Pointer down rather than click: the input keeps focus
                      // through a mousedown, so the palette does not have to
                      // survive a blur it would otherwise close on.
                      onMouseDown={(event) => {
                        event.preventDefault();
                        go(item);
                      }}
                      onMouseEnter={() => setActive(position)}
                    >
                      <span className="block truncate font-medium">{item.label}</span>
                      {item.hint ? (
                        <span className="block truncate text-sm text-[color:var(--color-muted)]">{item.hint}</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}

          {tooShort ? (
            <li role="presentation" className="px-4 py-6 text-center text-sm text-[color:var(--color-muted)]">
              {/* `min`, not `count`: i18next reads `count` as a plural
                  selector and would look for a key that does not exist. */}
              {t('commandPalette.keepTyping', { min: MIN_TERM_LENGTH })}
            </li>
          ) : null}
          {failed ? (
            <li role="presentation" className="px-4 py-3 text-center text-sm text-[color:var(--color-danger)]">
              {t('commandPalette.failed')}
            </li>
          ) : null}
          {empty && !failed ? (
            <li role="presentation" className="px-4 py-6 text-center text-sm text-[color:var(--color-muted)]">
              {t('commandPalette.noResults')}
            </li>
          ) : null}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
